/** Reserved local UI ports that are not same-origin gateway stack mounts. */
const RESERVED_UI_PORTS = new Set([5173, 5174]);

/**
 * Same-origin gateway path mount for a branch internal UI port, e.g. `/5176`.
 * Matches `parseGatewayStackMount` in `scripts/lib/dev-stack-gateway-plugin.mjs`.
 */
export function devStackMountPrefixFromPathname(pathname: string): string | null {
  const match = pathname.match(/^\/(\d{4})(?=\/|$)/);
  if (!match) {
    return null;
  }
  const port = Number(match[1]);
  if (!Number.isFinite(port) || RESERVED_UI_PORTS.has(port)) {
    return null;
  }
  if (port < 5175 || port > 5299) {
    return null;
  }
  return `/${port}`;
}

function isLocalDevGatewayOrigin(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const { hostname, port } = window.location;
  if (hostname !== "localhost" && hostname !== "127.0.0.1") {
    return false;
  }
  return port === "5173" || port === "";
}

/** BrowserRouter basename for path-mounted dev stacks behind the 5173 gateway. */
export function hubRouterBasename(): string | undefined {
  if (import.meta.env?.DEV && typeof window !== "undefined") {
    const fromLocation = devStackMountPrefixFromPathname(window.location.pathname);
    // On the gateway, the visible `/5176/` prefix is authoritative — a bundle baked
    // with BASE_URL `/` would otherwise leave React Router at the wrong path.
    if (fromLocation && isLocalDevGatewayOrigin()) {
      return fromLocation;
    }
  }

  const fromViteBase = (import.meta.env?.BASE_URL ?? "/").replace(/\/$/, "");
  if (fromViteBase) {
    return fromViteBase;
  }

  if (import.meta.env?.DEV && typeof window !== "undefined") {
    const fromLocation = devStackMountPrefixFromPathname(window.location.pathname);
    if (fromLocation) {
      return fromLocation;
    }
  }

  return undefined;
}

/** True when the hub SPA is served under a gateway stack mount on localhost. */
export function isGatewayStackMountPath(pathname: string): boolean {
  return devStackMountPrefixFromPathname(pathname) !== null;
}

/**
 * Strip a gateway stack mount prefix from a request path so internal Vite middleware
 * (for example `/api` proxy) can match paths like `/5176/api/me`.
 */
export function stripDevStackMountPrefix(pathname: string): string {
  const mount = devStackMountPrefixFromPathname(pathname);
  if (!mount) {
    return pathname;
  }
  const rest = pathname.slice(mount.length);
  if (rest === "") {
    return "/";
  }
  return rest.startsWith("/") ? rest : `/${rest}`;
}
